import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Header } from "@/components/Header";
import { apiError } from "@/lib/api";
import { useCourses, useDeleteCourse, useGenerateCourse } from "@/hooks/useCourses";
import { courseService } from "@/services/courseService";
import { usePublicSettings } from "@/hooks/useSettings";
import { useAuth } from "@/context/AuthContext";
import { usePlugins } from "@/plugins/usePlugins";
import { expertService } from "@/services/expertService";
import { ExpertCard } from "@/components/ExpertCard";
import { Button } from "@/components/ui/button";
import { GettingStartedCard } from "@/components/GettingStartedCard";
import { PersonalpediaContent } from "@/pages/Personalpedia";
import { FaqContent } from "@/pages/FAQ";
import { GuidedTour, runTour } from "@/components/tour/GuidedTour";
import { LEARNER_DASHBOARD_TOUR, SCHOLAR_DASHBOARD_TOUR } from "@/components/tour/tourSteps";
import { Plus, Play, Trash2, Zap, Coffee, Layers, Crown, Coins, Sparkles, ArrowRight, Users, GraduationCap, Search, Lock, PlayCircle, BrainCircuit, MessageCircleQuestion } from "lucide-react";

const ICONS = { overview: Zap, lunch: Coffee, comprehensive: Layers };
const LEN_LABEL = { overview: "2-min overview", lunch: "15-min course", comprehensive: "Full training" };
const LOADING_MSGS = [
  "Researching your topic...",
  "Designing the curriculum...",
  "Writing the script...",
  "Structuring chapters...",
  "Finalizing your course...",
];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: settings } = usePublicSettings();

  const { data: courses = [], isLoading: loading } = useCourses();
  const deleteMutation = useDeleteCourse({
    onSuccess: () => toast.success("Course removed"),
    onError: (e) => toast.error(apiError(e)),
  });

  const isMember = user?.plan_type === "scholar" || user?.plan_type === "enterprise" || user?.role === "admin";
  const ratio = settings?.coin_usd_ratio || 5;
  const coins = user?.credit_balance ?? 0;
  const usd = (coins / ratio).toFixed(2);
  const serviceCosts = settings?.service_costs || {};
  const costOf = (key) => serviceCosts[key] ?? 1;
  const [tab, setTab] = useState("creator");
  const { isPluginEnabled } = usePlugins();
  const expertOn = isPluginEnabled("expertmeai");
  const [newTopic, setNewTopic] = useState("");
  const [newLength, setNewLength] = useState("overview");
  const [msgIdx, setMsgIdx] = useState(0);

  // One-click, same-page generation (mirrors the Landing Page flow).
  const generateMutation = useGenerateCourse({
    onSuccess: (data) => navigate(`/course/${data.course.id}`),
    onError: (e) => {
      toast.error(apiError(e));
      if (e?.response?.status === 402) navigate("/pricing");
    },
  });
  const generating = generateMutation.isPending;

  useEffect(() => {
    if (!generating) return;
    const t = setInterval(() => setMsgIdx((i) => (i + 1) % LOADING_MSGS.length), 1800);
    return () => clearInterval(t);
  }, [generating]);

  const startCourse = () => {
    const t = newTopic.trim();
    if (!t) { toast.error("Please enter a topic"); return; }
    if (!isMember && coins < costOf(newLength)) {
      toast.error("Not enough coins. Refill your wallet or go Scholar for unlimited.");
      navigate("/pricing");
      return;
    }
    toast.dismiss();
    generateMutation.mutate({ topic: t, length: newLength });
  };

  // Onboarding nudge (brand-new accounts) + "Take the tour" replay.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const welcome = searchParams.get("welcome") === "1";
    const replay = searchParams.get("tour") === "1";
    if (!welcome && !replay) return;
    const key = `lm_tour_dash_${isMember ? "scholar" : "learner"}_v1`;
    if (welcome && !isMember) {
      toast.dismiss();
      toast.success(`Welcome to Learn Me${user?.name ? ", " + user.name : ""}! Here's a quick tour to get you started.`);
    }
    const timer = setTimeout(() => runTour(key), 1100);
    const next = new URLSearchParams(searchParams);
    next.delete("welcome"); next.delete("tour");
    setSearchParams(next, { replace: true });
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: continueCourse } = useQuery({
    queryKey: ["continue-course"],
    queryFn: courseService.getContinue,
    staleTime: 30_000,
  });

  const remove = (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    deleteMutation.mutate(id);
  };

  if (generating) {
    return (
      <div className="fixed inset-0 z-[60] bg-foreground text-background flex flex-col items-center justify-center text-grain dojo-page" data-testid="dashboard-generating-screen">
        <div className="h-14 w-14 border-2 border-primary border-t-transparent animate-spin rounded-full mb-8" />
        <h2 className="font-display text-3xl font-bold tracking-tight mb-2">Building your course</h2>
        <p className="text-background/70">{LOADING_MSGS[msgIdx]}</p>
        <p className="mt-6 text-sm text-background/50 max-w-md text-center">Crafting "{newTopic}" — this takes a few seconds.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background dojo-page">
      <Header />
      <GuidedTour
        steps={isMember ? SCHOLAR_DASHBOARD_TOUR : LEARNER_DASHBOARD_TOUR}
        storageKey={`lm_tour_dash_${isMember ? "scholar" : "learner"}_v1`}
        auto={false}
      />
      <main className="max-w-7xl mx-auto px-6 md:px-8 py-12">
        <div className="mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-2">Your library</p>
            <h1 className="font-display text-4xl font-extrabold tracking-tighter">Hey {user?.name} 👋</h1>
            <p className="text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
              {courses.length} course{courses.length !== 1 && "s"} ·
              <span className="inline-flex items-center gap-1 font-semibold">
                {isMember ? <><Crown className="h-4 w-4 text-primary" /> {user?.plan_label}</> : `${user?.plan_label} plan`}
              </span>
              {!isMember && (
                <span id="tour-wallet" className="inline-flex items-center gap-1 font-semibold text-primary" data-testid="credit-balance">
                  · <Coins className="h-4 w-4" /> {coins} Coins (${usd})
                </span>
              )}
            </p>
          </div>
        </div>

        {continueCourse && (
          <button
            onClick={() => navigate(`/course/${continueCourse.id}`)}
            className="w-full text-left mb-8 group relative overflow-hidden border border-primary/40 bg-gradient-to-r from-primary/15 via-primary/5 to-transparent p-5 sm:p-6 hover:border-primary transition-colors"
            data-testid="continue-mastering-banner"
          >
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 shrink-0 bg-primary/20 flex items-center justify-center rounded-full group-hover:scale-105 transition-transform">
                <PlayCircle className="h-6 w-6 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-primary mb-0.5">Continue mastering</p>
                <h3 className="font-display text-lg sm:text-xl font-bold leading-tight truncate" data-testid="continue-course-title">
                  {continueCourse.title}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {continueCourse.length_label}
                  {continueCourse.section_count ? ` · Chapter ${Math.min(continueCourse.section_idx + 1, continueCourse.section_count)} of ${continueCourse.section_count}` : ""}
                </p>
              </div>
              <span className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-primary shrink-0">
                Resume <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </div>
          </button>
        )}

        <div className="flex items-center gap-1 border-b border-border mb-10 overflow-x-auto" data-testid="dashboard-tabs">
          {[
            { key: "creator", label: "Course creator", icon: Sparkles },
            { key: "library", label: "Library", icon: Layers },
            { key: "personalpedia", label: "Personalpedia", icon: BrainCircuit },
            { key: "faq", label: "FAQ", icon: MessageCircleQuestion },
            ...(expertOn ? [
              { key: "experts", label: "Collected Experts", icon: Users },
              { key: "courses", label: "Expert Courses", icon: GraduationCap },
            ] : []),
          ].map((t) => (
            <button key={t.key} id={`tour-tab-${t.key}`} onClick={() => setTab(t.key)} data-testid={`dashboard-tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 h-11 text-sm font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${tab === t.key ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Course creator tab — main create block (mirrors homepage) */}
        {tab === "creator" && (
        <section className="mb-10" data-testid="dashboard-create">
          <div className="border border-border bg-card p-6 sm:p-8">
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mb-1">What do you want to learn?</h2>
            <p className="text-muted-foreground text-sm mb-5">Type any topic and get a narrated custom course in seconds.</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1 flex items-center gap-2 border border-input bg-background px-4 h-12 focus-within:border-primary transition-colors">
                <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                <input value={newTopic} onChange={(e) => setNewTopic(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && startCourse()}
                  placeholder="e.g. Quantum computing, Stoicism, React hooks…"
                  className="flex-1 bg-transparent outline-none text-sm" data-testid="dashboard-topic-input" id="tour-dash-search" />
              </div>
              <Button id="tour-dash-create" onClick={startCourse} className="rounded-none h-12 px-6 bg-primary hover:opacity-90 gap-2" data-testid="new-course-button">
                <Plus className="h-4 w-4" /> Create course
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-4">
              {[
                { key: "overview", icon: Zap, label: "2 min" },
                { key: "lunch", icon: Coffee, label: "15 min" },
                { key: "comprehensive", icon: Layers, label: "Full training" },
              ].map((l) => (
                <button key={l.key} id={`tour-len-${l.key}`} onClick={() => setNewLength(l.key)} data-testid={`dashboard-length-${l.key}`}
                  className={`inline-flex items-center gap-1.5 px-3 h-9 border text-sm font-semibold transition-colors ${newLength === l.key ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground hover:text-foreground"}`}>
                  <l.icon className="h-4 w-4" /> {l.label}
                </button>
              ))}
            </div>
          </div>
        </section>
        )}

        {expertOn && tab === "experts" && <CollectedExpertsPanel isMember={isMember} navigate={navigate} />}
        {expertOn && tab === "courses" && <ExpertCoursesPanel isMember={isMember} navigate={navigate} />}
        {tab === "personalpedia" && <PersonalpediaContent />}
        {tab === "faq" && <FaqContent />}

        {tab === "library" && (<>
        <div className="mb-12">
          <GettingStartedCard />
        </div>

        {loading ? (
          <div className="py-20 flex justify-center">
            <div className="h-8 w-8 border-2 border-primary border-t-transparent animate-spin rounded-full" />
          </div>
        ) : courses.length === 0 ? (
          <div className="border border-dashed border-border bg-card p-16 text-center" data-testid="empty-state">
            <h3 className="font-display text-xl font-bold mb-2">No courses yet</h3>
            <p className="text-muted-foreground mb-6">Generate your first course and it'll appear here.</p>
            <Button onClick={() => setTab("creator")} className="rounded-none bg-primary hover:opacity-90">Create a course</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="course-grid">
            {courses.map((c, i) => {
              const Icon = ICONS[c.length] || Zap;
              return (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }}
                >
                  <Link to={`/course/${c.id}`} className="block group border border-border bg-card p-6 hover:border-foreground hover:-translate-y-1 transition-all" data-testid={`course-card-${c.id}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="h-10 w-10 bg-secondary flex items-center justify-center">
                        <Icon className="h-5 w-5" />
                      </div>
                      <button onClick={(e) => remove(c.id, e)} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`delete-course-${c.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <h3 className="font-display text-lg font-bold leading-tight mb-1 line-clamp-2">{c.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{c.subtitle}</p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="uppercase tracking-wider font-semibold text-muted-foreground" data-testid={`course-duration-${c.id}`}>
                        {c.length_label}{c.duration_minutes ? ` · ${c.duration_source === "measured" ? "" : "≈"}${c.duration_minutes} min` : ""}
                      </span>
                      <span className="inline-flex items-center gap-1 text-primary font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                        <Play className="h-3.5 w-3.5" /> Watch
                      </span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
        </>)}
      </main>
    </div>
  );
}


function UpgradePrompt({ title, body }) {
  return (
    <div className="border border-dashed border-border bg-card p-16 text-center" data-testid="experts-upgrade-prompt">
      <Lock className="h-7 w-7 mx-auto mb-3 text-primary" />
      <h3 className="font-display text-xl font-bold mb-2">{title}</h3>
      <p className="text-muted-foreground mb-6 max-w-md mx-auto">{body}</p>
      <Link to="/pricing"><Button className="rounded-none bg-primary hover:opacity-90 gap-2"><Crown className="h-4 w-4" /> Become a Scholar</Button></Link>
    </div>
  );
}

function ExpertsEmpty({ navigate }) {
  return (
    <div className="border border-dashed border-border bg-card p-16 text-center" data-testid="experts-empty">
      <Search className="h-7 w-7 mx-auto mb-3 text-muted-foreground" />
      <h3 className="font-display text-xl font-bold mb-2">Nothing here yet</h3>
      <p className="text-muted-foreground mb-6">Search ExpertMeAI to discover experts and build deep-dive courses.</p>
      <Button onClick={() => navigate("/experts")} className="rounded-none bg-primary hover:opacity-90 gap-2"><Sparkles className="h-4 w-4" /> Explore ExpertMeAI</Button>
    </div>
  );
}

function Spinner() {
  return <div className="py-20 flex justify-center"><div className="h-8 w-8 border-2 border-primary border-t-transparent animate-spin rounded-full" /></div>;
}

function CollectedExpertsPanel({ isMember, navigate }) {
  const qc = useQueryClient();
  const { data: experts = [], isLoading } = useQuery({
    queryKey: ["experts", "collected"], queryFn: expertService.listCollected, enabled: isMember,
  });
  const remove = useMutation({
    mutationFn: (e) => expertService.removeCollected(e.id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["experts", "collected"] }); toast.success("Removed from your list"); },
    onError: (e) => toast.error(apiError(e)),
  });
  if (!isMember) return <UpgradePrompt title="Collected Experts is a Scholar feature" body="Upgrade to build a personal roster of experts and turn any of them into a masterclass." />;
  if (isLoading) return <Spinner />;
  if (!experts.length) return <ExpertsEmpty navigate={navigate} />;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="collected-experts-grid">
      {experts.map((e, i) => (
        <ExpertCard key={e.id} expert={e} topic={e.topic} index={i} isMember onRemove={() => remove.mutate(e)} />
      ))}
    </div>
  );
}

function ExpertCoursesPanel({ isMember, navigate }) {
  const { data: courses = [], isLoading } = useQuery({
    queryKey: ["experts", "courses"], queryFn: expertService.listCourses, enabled: isMember,
  });
  if (!isMember) return <UpgradePrompt title="Expert Courses is a Scholar feature" body="Upgrade to generate narrated 5-module deep-dives on any expert you discover." />;
  if (isLoading) return <Spinner />;
  if (!courses.length) return <ExpertsEmpty navigate={navigate} />;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="expert-courses-grid">
      {courses.map((c, i) => (
        <motion.div key={c.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }}>
          <Link to={`/course/${c.id}`} className="block group border border-border bg-card p-6 hover:border-foreground hover:-translate-y-1 transition-all" data-testid={`expert-course-card-${c.id}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="h-10 w-10 bg-secondary flex items-center justify-center"><GraduationCap className="h-5 w-5" /></div>
              <span className="text-[10px] uppercase tracking-wider font-bold text-primary">Expert Deep-Dive</span>
            </div>
            <h3 className="font-display text-lg font-bold leading-tight mb-1 line-clamp-2">{c.title}</h3>
            <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{c.subtitle}</p>
            <div className="flex items-center justify-between text-xs">
              <span className="uppercase tracking-wider font-semibold text-muted-foreground">{c.section_count} modules{c.duration_minutes ? ` · ≈${c.duration_minutes} min` : ""}</span>
              <span className="inline-flex items-center gap-1 text-primary font-semibold opacity-0 group-hover:opacity-100 transition-opacity"><Play className="h-3.5 w-3.5" /> Watch</span>
            </div>
          </Link>
        </motion.div>
      ))}
    </div>
  );
}
